import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import StatsCard from '../../components/ui/StatsCard';
import SubcontractsTable from './components/SubcontractsTable';
import StatusFilterDropdown from '../../components/ui/StatusFilterDropdown';
import AddContractModal from './components/AddContractModal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import subcontractService from '../../services/subcontractService';
import { projectService } from '../../services/projectService';
import { getSubcontractSummary } from '../../services/subcontractFinancialService';
import subcontractChangeOrderService from '../../services/subcontractChangeOrderService';
import useTranslation from '../../hooks/useTranslation';
import { formatCurrency as fmtCAD } from '../../utils/formatCurrency';

const ALLOWED_SORT_KEYS = ['cost_code', 'subcontractor', 'trade', 'contract_value', 'status'];
const ALLOWED_SORT_DIRS = ['asc', 'desc'];

const ContractsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const initialProject = location?.state?.project;

  const getInitialSort = () => {
    const sortKey = searchParams?.get('sort');
    const sortDir = searchParams?.get('dir');
    if (ALLOWED_SORT_KEYS?.includes(sortKey) && ALLOWED_SORT_DIRS?.includes(sortDir)) {
      return { key: sortKey, direction: sortDir };
    }
    return { key: 'cost_code', direction: 'asc' };
  };

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(initialProject || null);
  const [showAddContractModal, setShowAddContractModal] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [revisedContractMap, setRevisedContractMap] = useState({});
  const [coCountMap, setCoCountMap] = useState({});
  const [coNumberMap, setCoNumberMap] = useState({});
  const [sortConfig, setSortConfig] = useState(getInitialSort());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contractToDelete, setContractToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteBlockers, setDeleteBlockers] = useState(null); // { total, avenants, ... }
  const [toastMsg, setToastMsg] = useState(null);             // { type: 'success'|'error', text }
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingContract, setEditingContract] = useState(null);

  // 'completed' stays in the FILTER (6 legacy contracts must remain findable),
  // even though it is no longer offered in the status-change menu.
  const contractStatusFilterOptions = [
    { value: 'all', label: t('contracts.filters.allStatus'), dot: null },
    { value: 'to_assign', label: t('contracts.statusOptions.toAssign'), dot: '#9ca3af' },
    { value: 'draft', label: t('contracts.statusOptions.draft'), dot: '#facc15' },
    { value: 'sent', label: t('contracts.statusOptions.sent'), dot: '#3b82f6' },
    { value: 'signed', label: t('contracts.statusOptions.signed'), dot: '#22c55e' },
    { value: 'completed', label: t('contracts.statusOptions.completed'), dot: '#6b7280' },
    { value: 'cancelled', label: t('contracts.statusOptions.cancelled'), dot: '#f87171' },
  ];

  useEffect(() => {
    const sortKey = searchParams?.get('sort');
    const sortDir = searchParams?.get('dir');
    const isValid = ALLOWED_SORT_KEYS?.includes(sortKey) && ALLOWED_SORT_DIRS?.includes(sortDir);
    if (!isValid) {
      setSearchParams({ sort: 'cost_code', dir: 'asc' }, { replace: true });
    }
  }, []);

  useEffect(() => {
    const sortKey = searchParams?.get('sort');
    const sortDir = searchParams?.get('dir');
    if (ALLOWED_SORT_KEYS?.includes(sortKey) && ALLOWED_SORT_DIRS?.includes(sortDir)) {
      setSortConfig(prev => {
        if (prev?.key === sortKey && prev?.direction === sortDir) return prev;
        return { key: sortKey, direction: sortDir };
      });
    }
  }, [searchParams]);

  const handleSort = (key) => {
    if (!ALLOWED_SORT_KEYS?.includes(key)) return;
    const direction = sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
    setSearchParams({ sort: key, dir: direction }, { replace: true });
  };

  useEffect(() => {
    if (id) {
      fetchContracts();
      if (!initialProject) {
        projectService?.getById(id)?.then(data => setProject(data))?.catch(err => console.error('Error fetching project:', err));
      }
    }
  }, [id]);

  const fetchContracts = async () => {
    setLoading(true);
    try {
      const contractsData = await subcontractService?.getByProjectId(id);
      setContracts(contractsData || []);

      if (contractsData?.length > 0) {
        const summaries = await Promise.all(
          contractsData?.map(async (c) => {
            const summary = await getSubcontractSummary(c?.id);
            return { id: c?.id, revisedContract: summary?.revisedContract ?? 0 };
          })
        );
        const map = {};
        summaries?.forEach(({ id, revisedContract }) => { map[id] = revisedContract; });
        setRevisedContractMap(map);
      }

      // Build CO count map: subcontract_id → number of approved COs
      const allCOs = await subcontractChangeOrderService?.getByProjectId(id);
      const countMap = {};
      const numMap = {};
      (allCOs || [])?.forEach(co => {
        if (co?.subcontractId && co?.status === 'approved') {
          countMap[co?.subcontractId] = (countMap?.[co?.subcontractId] || 0) + 1;
          if (!numMap?.[co?.subcontractId]) numMap[co?.subcontractId] = [];
          if (co?.coNumber) numMap?.[co?.subcontractId]?.push(co?.coNumber);
        }
      });
      setCoCountMap(countMap);
      setCoNumberMap(numMap);
    } catch (error) {
      console.error('Error fetching contracts data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddContract = async (contractData) => {
    const newContract = await subcontractService?.create(contractData);
    if (!newContract) throw new Error('Failed to create contract');
    await fetchContracts();
  };

  const handleEditContract = (contract) => {
    setEditingContract(contract);
    setShowAddContractModal(true);
  };

  const handleSaveContract = async (contractData) => {
    if (editingContract) {
      // Edit mode
      const updated = await subcontractService?.update(editingContract?.id, contractData);
      if (!updated) throw new Error('Failed to update contract');
      await fetchContracts();
      return updated;
    } else {
      // Create mode
      const newContract = await subcontractService?.create(contractData);
      if (!newContract) throw new Error('Failed to create contract');
      await fetchContracts();
      return newContract;
    }
  };

  const handleUpdateContract = async (contractId, values) => {
    try {
      const updatedContract = await subcontractService?.update(contractId, values);
      if (updatedContract) {
        setContracts(prev => prev?.map(contract =>
          contract?.id === contractId ? updatedContract : contract
        ));
      }
    } catch (error) {
      console.error('Error updating contract:', error);
    }
  };

  const handleNoteUpdate = async (contractId, noteText) => {
    try {
      const updatedContract = await subcontractService?.update(contractId, { notes: noteText?.trim() || null });
      if (updatedContract) {
        setContracts(prev => prev?.map(contract =>
          contract?.id === contractId ? updatedContract : contract
        ));
      }
      return true;
    } catch (error) {
      console.error('Error updating note:', error);
      return false;
    }
  };

  const showToast = (type, text) => {
    setToastMsg({ type, text });
    setTimeout(() => setToastMsg(null), 3500);
  };

  const closeDeleteDialogs = () => {
    setShowDeleteModal(false);
    setDeleteBlockers(null);
    setContractToDelete(null);
    setIsDeleting(false);
  };

  // Avant toute suppression : on recense ce qui est rattache au contrat.
  // Un contrat relie a quoi que ce soit n'est pas supprimable.
  const handleDeleteContract = async (contractId) => {
    const contract = contracts?.find(c => c?.id === contractId) || null;
    if (!contract) return;
    setContractToDelete(contract);
    setDeleteBlockers(null);
    setIsDeleting(true);
    setShowDeleteModal(true);
    try {
      const blockers = await subcontractService?.getDeleteBlockers(contract?.id);
      setDeleteBlockers(blockers);
    } catch (error) {
      console.error('Error checking contract links:', error);
      closeDeleteDialogs();
      showToast('error', t('subcontracts.delete.error'));
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmDeleteContract = async () => {
    if (!contractToDelete || isDeleting) return;
    // Garde-fou : jamais de suppression si un lien subsiste.
    if (!deleteBlockers || deleteBlockers?.total > 0) return;
    const target = contractToDelete;
    setIsDeleting(true);
    try {
      const success = await subcontractService?.delete(target?.id);
      if (success) {
        setContracts(prev => prev?.filter(contract => contract?.id !== target?.id));
        closeDeleteDialogs();
        showToast('success', t('subcontracts.delete.success'));
      } else {
        closeDeleteDialogs();
        showToast('error', t('subcontracts.delete.error'));
      }
    } catch (error) {
      console.error('Error deleting contract:', error);
      closeDeleteDialogs();
      showToast('error', t('subcontracts.delete.error'));
    }
  };

  // Liste, en clair, uniquement les categories effectivement liees.
  const blockerLines = () => {
    if (!deleteBlockers) return [];
    const map = [
      ['avenants', 'blockedAvenants'],
      ['subInvoices', 'blockedSubInvoices'],
      ['subInvoiceLines', 'blockedSubInvoiceLines'],
      ['extras', 'blockedExtras'],
      ['extraLines', 'blockedExtraLines'],
      ['corrections', 'blockedCorrections'],
      ['quittances', 'blockedQuittances'],
    ];
    return map
      ?.filter(([field]) => (deleteBlockers?.[field] || 0) > 0)
      ?.map(([field, key]) =>
        t(`subcontracts.delete.${key}`, { count: deleteBlockers?.[field] })
      );
  };

  const calculateKPIs = () => {
    const totalContracts = contracts?.length || 0;
    const totalValue = contracts?.reduce((sum, c) => sum + (parseFloat(c?.contractValue) || 0), 0) || 0;
    const pendingSignatureContracts = contracts?.filter(c => c?.status === 'pending_signature')?.length || 0;
    const signedContracts = contracts?.filter(c => c?.status === 'signed')?.length || 0;
    return { totalContracts, totalValue, pendingSignatureContracts, signedContracts };
  };

  const kpis = calculateKPIs();

  // Bloque des qu'au moins un element reste rattache au contrat
  const isBlocked = !!deleteBlockers && deleteBlockers?.total > 0;

  const sortedContracts = [...(contracts || [])]?.sort((a, b) => {
    if (!sortConfig?.key) return 0;
    let aValue, bValue;
    switch (sortConfig?.key) {
      case 'cost_code': aValue = a?.projectCost?.costCode?.toLowerCase() || ''; bValue = b?.projectCost?.costCode?.toLowerCase() || ''; break;
      case 'subcontractor': aValue = a?.subcontractor?.companyName?.toLowerCase() || ''; bValue = b?.subcontractor?.companyName?.toLowerCase() || ''; break;
      case 'trade': aValue = a?.subcontractor?.trade?.toLowerCase() || ''; bValue = b?.subcontractor?.trade?.toLowerCase() || ''; break;
      case 'contract_value': aValue = parseFloat(a?.contractValue) || 0; bValue = parseFloat(b?.contractValue) || 0; break;
      case 'status': aValue = a?.status || ''; bValue = b?.status || ''; break;
      default: return 0;
    }
    if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const statusFilteredContracts = statusFilter === 'all'
    ? sortedContracts
    : sortedContracts?.filter(c => (c?.status || 'to_assign') === statusFilter);

  const filteredContracts = searchQuery?.trim()
    ? statusFilteredContracts?.filter(c => {
        const q = searchQuery?.toLowerCase()?.trim();
        const subName = c?.subcontractor?.companyName?.toLowerCase() || '';
        const contractNum = c?.contractNumber?.toLowerCase() || c?.id?.toLowerCase() || '';
        const costCode = c?.projectCost?.costCode?.toLowerCase() || '';
        const trade = c?.subcontractor?.trade?.toLowerCase() || '';
        const division = c?.projectCost?.description?.toLowerCase() || '';
        return subName?.includes(q) || contractNum?.includes(q) || costCode?.includes(q) || trade?.includes(q) || division?.includes(q);
      })
    : statusFilteredContracts;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Icon name="Loader2" size={48} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="pt-0 pb-0 px-0 h-full flex flex-col">
      {/* Header Block with KPI Cards on Same Row */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-6">
          {/* Left Side: Title and Subtitle */}
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="FileText" size={20} className="text-primary" />
              <h1 className="text-lg font-semibold text-gray-900">
                {t('contracts.title')}
              </h1>
            </div>

            {/* Line 2: Breadcrumb - Project Number - Project Name */}
            <div className="flex items-center gap-1 mb-1">
              <span
                onClick={() => navigate('/project-detail')}
                className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
              >
                {project?.projectNumber || t('common.na')}
              </span>
              <span className="text-sm text-primary font-normal">-</span>
              <span
                onClick={() => navigate('/project-detail')}
                className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
              >
                {project?.name || t('common.loading')}
              </span>
            </div>

            <p className="text-xs text-gray-500">
              {t('contracts.subtitle')}
            </p>
          </div>

          {/* Right Side: 4 KPI Cards in Horizontal Layout */}
          <div className="flex-1 flex items-center justify-end gap-2">
            {/* 1) Total Contracts */}
            <StatsCard
              title={t('contracts.kpi.totalContracts')}
              value={kpis?.totalContracts}
              sublabel=""
              iconName="FileText"
              iconColor="text-blue-500"
              valueColor="text-blue-500"
              titleWeight="font-medium"
            />

            {/* 2) Total Value */}
            <StatsCard
              title={t('contracts.kpi.totalValue')}
              value={fmtCAD(kpis?.totalValue || 0)}
              sublabel=""
              iconName="DollarSign"
              iconColor="text-green-500"
              valueColor="text-gray-900"
              titleWeight="font-medium"
            />

            {/* 3) Pending Signature */}
            <StatsCard
              title={t('contracts.kpi.pendingSignature')}
              value={kpis?.pendingSignatureContracts}
              sublabel=""
              iconName="Clock"
              iconColor="text-orange-500"
              valueColor="text-gray-900"
              titleWeight="font-medium"
            />

            {/* 4) Signed */}
            <StatsCard
              title={t('contracts.kpi.signed')}
              value={kpis?.signedContracts}
              sublabel=""
              iconName="CheckCircle2"
              iconColor="text-green-500"
              valueColor="text-green-600"
              titleWeight="font-medium"
            />
          </div>
        </div>
      </div>

      {/* Search + Add Contract Row */}
      <div className="flex items-center justify-between gap-3 mb-4">
        {/* Search Box + Status filter */}
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              placeholder={t('contracts.filters.searchPlaceholder')}
              className="w-full h-8 pl-8 pr-3 text-xs rounded-md border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Icon name="X" size={12} />
              </button>
            )}
          </div>
          <StatusFilterDropdown
            options={contractStatusFilterOptions}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </div>

        {/* Add Contract Button */}
        <button
          onClick={() => setShowAddContractModal(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors shadow-sm"
        >
          <Icon name="Plus" size={13} />
          {t('contracts.table.addContract')}
        </button>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden min-h-0 flex flex-col">
        {contracts?.length === 0 ? (
          <div className="p-12 text-center">
            <Icon name="FileText" size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="text-sm text-gray-500">{t('contracts.empty.emptyTitle')}</p>
            <p className="text-xs text-gray-400 mt-1">{t('contracts.empty.emptyDescription')}</p>
          </div>
        ) : (
          <div className="min-h-0 overflow-y-auto overflow-x-auto overscroll-contain">
            <SubcontractsTable
              subcontracts={filteredContracts}
              onUpdate={handleUpdateContract}
              onDelete={handleDeleteContract}
              onNoteUpdate={handleNoteUpdate}
              onEdit={handleEditContract}
              sortConfig={sortConfig}
              onSort={handleSort}
              revisedContractMap={revisedContractMap}
              coCountMap={coCountMap}
              coNumberMap={coNumberMap}
              projectId={id}
            />
          </div>
        )}
      </div>

      <AddContractModal
        isOpen={showAddContractModal}
        onClose={() => { setShowAddContractModal(false); setEditingContract(null); }}
        onSubmit={handleSaveContract}
        projectId={id}
        initialData={editingContract}
      />
      <ConfirmDialog
        isOpen={showDeleteModal}
        onCancel={closeDeleteDialogs}
        onConfirm={isBlocked ? closeDeleteDialogs : confirmDeleteContract}
        title={isBlocked ? t('subcontracts.delete.blockedTitle') : t('subcontracts.delete.title')}
        message={
          isBlocked
            ? t('subcontracts.delete.blockedIntro')
            : deleteBlockers
              ? t('subcontracts.delete.message')
              : t('subcontracts.delete.checking')
        }
        confirmLabel={isBlocked ? t('subcontracts.delete.blockedClose') : t('subcontracts.delete.confirm')}
        cancelLabel={t('subcontracts.delete.cancel')}
        confirmVariant={isBlocked ? 'primary' : 'danger'}
        confirmOnly={isBlocked}
        icon={isBlocked ? 'AlertTriangle' : null}
        loading={isDeleting}
      >
        <div className="mt-2 rounded-md bg-gray-50 border border-gray-200 px-4 py-3">
          <p className="text-xs text-gray-500 mb-1">{t('subcontracts.delete.label')}</p>
          <p className="text-sm font-medium text-gray-900">
            {contractToDelete?.subcontractor?.companyName || contractToDelete?.name || ''}
          </p>
        </div>
        {isBlocked && (
          <ul className="mt-3 space-y-1">
            {blockerLines()?.map((line, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                {line}
              </li>
            ))}
          </ul>
        )}
      </ConfirmDialog>
      {toastMsg && (
        <div
          className={`fixed top-4 right-4 z-[100] flex items-center gap-2 px-4 py-3 text-white text-sm font-medium rounded-xl shadow-lg ${
            toastMsg?.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          <Icon name={toastMsg?.type === 'success' ? 'CheckCircle2' : 'AlertTriangle'} size={16} />
          {toastMsg?.text}
        </div>
      )}
    </div>
  );
};

export default ContractsPage;